import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import {CouponTypeEnum, SponsorTypeEnum} from 'modules/coupons/types';
import weekDaySelect from 'modules/notifications/util/week-day-select';
import useCreateSale from 'modules/products/hooks/use-create-sale';
import useProductDetails from 'modules/products/hooks/use-product-details';
import {
  PriceModeEnum,
  SaleProps,
  SaleTypeEnum,
  translatedSaleProps,
} from 'modules/products/types';
import React from 'react';
import {useParams} from 'react-router-dom';
import Creation from 'shared/layout/creation';
import useFormInputs from 'shared/migration-components/form/util';
import Loading from 'shared/migration-components/loading';

const CreateSale: React.FC = () => {
  const {id} = useParams();
  const {
    getSwitch,
    getSelect,
    getDatePicker,
    getNumberInput,
    getPriceInput,
    getMultiSelect,
  } = useFormInputs<SaleProps>();
  const {data, isLoading} = useProductDetails(id!);
  const {cityId, companyId} = useAccessControls();

  const handleSubmit = (data: SaleProps) => {
    return {
      ...data,
      productId: id,
      date: data.date
        ? new Date(`${data.date}T23:59:59`).toISOString()
        : undefined,
    };
  };

  if (isLoading) return <Loading />;

  return (
    <Creation<SaleProps>
      onSubmit={useCreateSale}
      defaultValues={{
        active: true,
        sponsorType: companyId ? SponsorTypeEnum.COMPANY : SponsorTypeEnum.CITY,
      }}
      {...{handleSubmit}}
      inputs={[
        getSelect({
          name: 'valueType',
          data: [
            {label: 'Valor', value: 'value'},
            {label: 'Porcentagem', value: 'percentage'},
          ],
          gridColumn: 6,
        }),
        getNumberInput({
          name: 'value',
          gridColumn: 6,
          hidden: (row) => row.valueType !== CouponTypeEnum.percentage,
        }),
        getPriceInput({
          name: 'value',
          gridColumn: 6,
          hidden: (row) => row.valueType !== CouponTypeEnum.value,
        }),
        getSelect({
          name: 'sizeId',
          data: (data?.sizes ?? []).map((size) => ({
            label: size.name,
            value: size.id,
          })),
          isLoading,
          hidden: () => data.priceMode === PriceModeEnum.fixed,
        }),
        getSelect({
          name: 'type',
          data: [
            {label: 'Única', value: SaleTypeEnum.oneTime},
            {label: 'Recorrente', value: SaleTypeEnum.periodical},
          ],
          gridColumn: 6,
        }),
        getMultiSelect({
          name: 'days',
          data: weekDaySelect,
          hidden: (data) => data.type !== SaleTypeEnum.periodical,
          gridColumn: 6,
        }),
        getDatePicker({
          name: 'date',
          hidden: (data) => data.type !== SaleTypeEnum.oneTime,
          gridColumn: 6,
        }),
        getSwitch({
          name: 'onePerOrder',
          hidden: (data) => data.type !== SaleTypeEnum.oneTime,
        }),
        getSwitch({
          name: 'onePerCustomer',
          hidden: (data) => data.type !== SaleTypeEnum.oneTime,
        }),
        getNumberInput({
          name: 'numberOfProducts',
          hidden: (data) => data.type !== SaleTypeEnum.oneTime,
        }),
        getSwitch({
          name: 'active',
        }),
        getSelect({
          name: 'sponsorType',
          data: [
            {label: 'Empresa', value: SponsorTypeEnum.COMPANY},
            {label: 'Cidade', value: SponsorTypeEnum.CITY},
          ],
          hidden: () => !!companyId,
        }),
      ]}
      translationKey={translatedSaleProps}
    />
  );
};

export default CreateSale;
